
import { motion } from "framer-motion";
import { TrendingDown } from "lucide-react";
import { cn, formatCurrency } from "@/lib/utils";
import { getPurityMultiplier } from "@/lib/pricing";

interface DealerPriceSectionProps {
  marketPrice: number;
  dealerBuyPrice: number;
  buySpread?: number | null;
  activated: boolean;
  isHomePage?: boolean;
  distance?: string;
  metalPurity?: string;
  metal?: "gold" | "silver";
}

export function DealerPriceSection({ 
  marketPrice, 
  dealerBuyPrice, 
  buySpread,
  activated = false,
  isHomePage = false,
  distance,
  metalPurity,
  metal = "gold"
}: DealerPriceSectionProps) {
  // Debug logs to track spread values
  console.log(`DealerPriceSection RECEIVED buySpread=${buySpread}, type=${typeof buySpread}`);
  
  // Spread è memorizzato come valore negativo nel DB, quindi serve il valore assoluto per la visualizzazione
  // Ma visualizziamo solo se il valore è effettivamente un numero
  const absSpread = typeof buySpread === 'number' && !isNaN(buySpread) ? 
    Math.abs(buySpread) : null;
    
  console.log(`DealerPriceSection calculated absSpread=${absSpread}`);
  
  // Il prezzo di mercato arriva già aggiustato con la purezza dal componente padre
  const marketPriceWithPurity = marketPrice;
  
  // Mostra il prezzo solo se il dealer è attivato e abbiamo dati reali
  const shouldShowPrice = activated === true && dealerBuyPrice > 0;
  
  return (
    <motion.div 
      className="dealer-price-section flex flex-col gap-1"
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="grid grid-cols-2 items-end gap-2">
        <div className="flex flex-col">
          <span className="text-xs font-medium text-muted-foreground">Prezzo di Mercato</span>
          <span className="text-sm font-medium">
            {marketPriceWithPurity > 0 ? formatCurrency(marketPriceWithPurity) : 'Non disponibile'}
          </span>
        </div>
        
        <div className="flex flex-col items-end">
          <span className="text-xs font-medium text-muted-foreground">Prezzo Acquisto</span>
          {shouldShowPrice ? (
            <span className="text-base font-bold text-gold-dark dark:text-gold-light">{formatCurrency(dealerBuyPrice)}</span>
          ) : (
            <span className="text-xs font-medium text-muted-foreground italic">Non disponibile</span>
          )}
        </div>
      </div>
      
      {typeof absSpread === 'number' && absSpread > 0 && shouldShowPrice && (
        <div className="grid grid-cols-2 items-end gap-2 mt-1 pt-1 border-t border-metal-light/20">
          <span className="text-xs font-medium text-muted-foreground text-left">Spread</span>
          <div className={cn(
            "flex items-end gap-1 text-xs font-medium rounded-full px-2 py-0.5 justify-end ml-auto",
            "text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400"
          )}>
            <TrendingDown className="h-3 w-3" />
            <span>-{absSpread}%</span>
          </div>
        </div>
      )}
      
      {metalPurity && (
        <div className="grid grid-cols-2 items-end gap-2 mt-1 pt-1 border-t border-metal-light/20">
          <span className="text-xs font-medium text-muted-foreground text-left">Purezza</span>
          <span className="text-xs font-medium text-right">
            {metal === "gold" ? "Oro" : "Argento"} {metalPurity}
          </span>
        </div>
      )}
      
      {distance && (
        <div className="grid grid-cols-2 items-end gap-2 mt-1 pt-1 border-t border-metal-light/20">
          <span className="text-xs font-medium text-muted-foreground text-left">Distanza</span>
          <span className="text-xs font-medium text-muted-foreground text-right">{distance}</span>
        </div>
      )}
    </motion.div>
  );
}
